import { unwrap, type components } from "@wilco/api";
import { api } from "./auth.ts";

/*
 * Названия к id из ответов склада, закупок и производства: в их контракте
 * у товара только productId (иногда с name), у поставщика supplierId, у
 * склада warehouseId. Справочник отвечает один раз на id за сессию
 * страницы: те же товары повторяются на каждом экране.
 */
type S = components["schemas"];
export type Warehouse = S["InventoryWarehouse"];

export interface ProductInfo {
  id: string;
  sku: string;
  name: string;
  /** Единицы в справочнике пока нет: штуки по умолчанию. */
  uom: string;
  consumable: boolean;
}

const products = new Map<string, Promise<ProductInfo>>();

export function productInfo(id: string, fallbackName?: string): Promise<ProductInfo> {
  let p = products.get(id);
  if (!p) {
    p = unwrap(api.GET("/api/v1/catalog/products/{id}", { params: { path: { id } } })).then(
      (x) => ({
        id,
        sku: x.code ?? id,
        name: x.version ? `${x.name} (${x.version})` : x.name,
        uom: "шт",
        consumable: x.tags.includes("CONSUMABLE"),
      }),
      // Справочник не знает id: показываем, что пришло, а не роняем экран.
      () => ({ id, sku: id, name: fallbackName ?? id, uom: "шт", consumable: false }),
    );
    products.set(id, p);
  }
  return p;
}

export async function productInfos(ids: Iterable<string>): Promise<Map<string, ProductInfo>> {
  const unique = [...new Set(ids)];
  const list = await Promise.all(unique.map((id) => productInfo(id)));
  return new Map(list.map((p) => [p.id, p]));
}

let warehouseList: Promise<Warehouse[]> | undefined;

export function warehouses(): Promise<Warehouse[]> {
  warehouseList ??= unwrap(api.GET("/api/v1/inventory/warehouses"));
  return warehouseList;
}

const partners = new Map<string, Promise<string>>();

/** Поставщик закупок это контрагент справочника (partners). */
export function partnerName(id: string): Promise<string> {
  let p = partners.get(id);
  if (!p) {
    p = unwrap(api.GET("/api/v1/partners/{id}", { params: { path: { id } } })).then(
      (x) => x.name,
      () => id,
    );
    partners.set(id, p);
  }
  return p;
}

const productSuppliers = new Map<string, Promise<{ id: string; name: string } | null>>();

/** Поставщик товара: первая строка поставщика в карточке справочника. */
export function productSupplier(productId: string): Promise<{ id: string; name: string } | null> {
  let p = productSuppliers.get(productId);
  if (!p) {
    p = unwrap(api.GET("/api/v1/catalog/products/{id}", { params: { path: { id: productId } } }))
      .then((x) => unwrap(api.GET("/api/v1/catalog/templates/{id}", { params: { path: { id: x.templateId } } })))
      .then(
        (t) => {
          const o = t.offers[0];
          return o ? { id: o.supplierId, name: o.supplierPartner ?? o.supplierId } : null;
        },
        () => null,
      );
    productSuppliers.set(productId, p);
  }
  return p;
}

/** После выхода кэш названий чужого арендатора не нужен. */
export function clearLookups(): void {
  products.clear();
  warehouseList = undefined;
  partners.clear();
  productSuppliers.clear();
}
